import { Fragment, ReactNode, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";

export type Crumb = { label: ReactNode; to?: string };

// Slot in <AppHeader> the breadcrumb portals into (official shadcn layout:
// SidebarTrigger + separator + breadcrumb on one header row).
export const BREADCRUMB_SLOT_ID = "app-breadcrumb-slot";

// Detail-page breadcrumb. See DESIGN.md "Details page". A parent crumb without `to`
// degrades to plain text (for entities that have no list route, e.g. apps).
// Call it anywhere in the page — it renders into the AppHeader slot; without a
// slot (isolated renders/tests) it falls back to rendering inline.
export const PageBreadcrumb = ({
  parent,
  current,
  className,
}: {
  parent?: Crumb | Crumb[];
  current: ReactNode;
  className?: string;
}) => {
  const [mounted, setMounted] = useState(false);
  const [slot, setSlot] = useState<HTMLElement | null>(null);
  useEffect(() => {
    setSlot(document.getElementById(BREADCRUMB_SLOT_ID));
    setMounted(true);
  }, []);

  const parents = parent ? (Array.isArray(parent) ? parent : [parent]) : [];
  const crumbs = (
    <Breadcrumb className={cn(!slot && "mb-4", className)}>
      <BreadcrumbList className="flex-nowrap">
        {parents.map((p, i) => (
          <Fragment key={i}>
            <BreadcrumbItem>
              {p.to ? (
                <BreadcrumbLink asChild>
                  <Link to={p.to}>{p.label}</Link>
                </BreadcrumbLink>
              ) : (
                <span>{p.label}</span>
              )}
            </BreadcrumbItem>
            <BreadcrumbSeparator />
          </Fragment>
        ))}
        <BreadcrumbItem className="min-w-0">
          <BreadcrumbPage className="max-w-[16rem] truncate sm:max-w-md">
            {current}
          </BreadcrumbPage>
        </BreadcrumbItem>
      </BreadcrumbList>
    </Breadcrumb>
  );

  if (!mounted) return null;
  if (!slot) return crumbs;
  return createPortal(
    <>
      <Separator orientation="vertical" className="h-4" />
      {crumbs}
    </>,
    slot,
  );
};

export default PageBreadcrumb;
